import { IRegionRepository } from '../../domain/interfaces/IRegionRepository';
import {
  Region,
  RegionDetail,
  RegionListItemDto,
  RegionDropdownDto,
  CreateRegionCommand,
  UpdateRegionCommand,
  RegionCrawlerStats
} from '../../domain/entities/Region';
import { PaginatedResult, PaginationParams } from '../../domain/common/PaginatedResult';
import { ApiClient } from '../api/ApiClient';
import { API_ENDPOINTS } from '../api/config';

/**
 * RegionRepository - Implementation of region repository
 *
 * Handles all API calls related to regions
 */
export class RegionRepository implements IRegionRepository {
  constructor(private readonly apiClient: ApiClient) {}

  /**
   * Get all regions with pagination (returns list items from API)
   */
  async getAll(params?: PaginationParams): Promise<PaginatedResult<RegionListItemDto>> {
    return this.apiClient.get<PaginatedResult<RegionListItemDto>>(API_ENDPOINTS.REGIONS.BASE, {
      params
    });
  }

  /**
   * Get active regions for dropdown/select components (lightweight)
   * Sprint 032: Returns only id, regionName, state, country
   */
  async getDropdownOptions(): Promise<RegionDropdownDto[]> {
    return this.apiClient.get<RegionDropdownDto[]>(API_ENDPOINTS.REGIONS.DROPDOWN);
  }

  /**
   * Get a single region by ID
   * Returns RegionDetail with subAreaCount (not full subAreas array)
   */
  async getById(id: string): Promise<RegionDetail> {
    return this.apiClient.get<RegionDetail>(API_ENDPOINTS.REGIONS.BY_ID(id));
  }

  /**
   * Create a new region (admin only)
   */
  async create(command: CreateRegionCommand): Promise<Region> {
    return this.apiClient.post<Region>(API_ENDPOINTS.REGIONS.BASE, command);
  }

  /**
   * Update an existing region (admin only)
   */
  async update(id: string, command: UpdateRegionCommand): Promise<Region> {
    return this.apiClient.put<Region>(API_ENDPOINTS.REGIONS.BY_ID(id), command);
  }

  /**
   * Delete a region (admin only)
   */
  async delete(id: string): Promise<void> {
    await this.apiClient.delete<void>(API_ENDPOINTS.REGIONS.BY_ID(id));
  }

  /**
   * Update subreddits for a region (admin only)
   */
  async updateSubreddits(id: string, subreddits: string[]): Promise<void> {
    await this.apiClient.put<void>(`${API_ENDPOINTS.REGIONS.BY_ID(id)}/subreddits`, {
      id,
      subreddits
    });
  }

  /**
   * Toggle region active status (admin only)
   */
  async toggleActive(id: string): Promise<Region> {
    return this.apiClient.post<Region>(`${API_ENDPOINTS.REGIONS.BY_ID(id)}/toggle-active`);
  }

  /**
   * Get crawler performance statistics for a region (admin only)
   * SESSION_400: Used by admin region detail page Statistics tab
   */
  async getCrawlerStats(id: string, days: number = 1): Promise<RegionCrawlerStats> {
    return this.apiClient.get<RegionCrawlerStats>(
      `${API_ENDPOINTS.REGIONS.BY_ID(id)}/crawler-stats?days=${days}`
    );
  }
}
